/* OSYN UI — interactions du design system (vanilla, transform/opacity, 60fps)
   Reveal iOS · sliders + pagination morphante · onglets coulissants · marquee +
   play/pause · compteurs · accordéon · toggle · quantité. Préfixe data-osyn-*
   (zéro collision Concept). Init idempotente : initOsyn(root) marque chaque
   élément (data-osyn-init) et se ré-exécute sur shopify:section:load. */
(() => {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  /* garde anti double-init : true une seule fois par élément */
  const once = (el) => {
    if (el.dataset.osynInit) return false;
    el.dataset.osynInit = '1';
    return true;
  };

  /* Libellés : locales du thème (groupe osyn.carousel, émis par snippets/osyn-head.liquid),
     repli FR si la page n'a pas le bloc. Aucun texte en dur ailleurs dans ce fichier. */
  const T = Object.assign({
    dot: 'Élément %i sur %n',
    roledescription: 'carrousel',
    label: 'Carrousel',
    pause: 'Mettre le défilement en pause',
    play: 'Relancer le défilement',
  }, (window.OsynI18n && window.OsynI18n.carousel) || {});
  const fmt = (str, map) => str.replace(/%([a-z])/g, (m, k) => (k in map ? map[k] : m));

  /* Écouteurs posés sur window/document par un composant : rattachés à SA section et libérés
     quand l'éditeur de thème la décharge (sinon chaque rechargement de section les empile). */
  const lifecycles = new Map();
  const signalFor = (el) => {
    const owner = el.closest('.shopify-section') || document.documentElement;
    let ctrl = lifecycles.get(owner);
    if (!ctrl) { ctrl = new AbortController(); lifecycles.set(owner, ctrl); }
    return ctrl.signal;
  };
  document.addEventListener('shopify:section:unload', (e) => {
    const ctrl = lifecycles.get(e.target);
    if (ctrl) { ctrl.abort(); lifecycles.delete(e.target); }
  });

  /* Cible d'alignement la plus proche (scrollLeft physique) : bord inline-start de chaque carte,
     mesuré au rect réel (robuste quel que soit le scroll-padding), bornée à la plage physique
     ([0,max] en LTR, [-max,0] en RTL). Fonction pure : partagée par le snap et l'amorce. */
  const snapTarget = (track, rtl) => {
    const kids = [...track.children];
    if (!kids.length) return track.scrollLeft;
    const tr = track.getBoundingClientRect();
    const cs = getComputedStyle(track);
    const padStart = parseFloat(cs.scrollPaddingInlineStart) || parseFloat(rtl ? cs.scrollPaddingRight : cs.scrollPaddingLeft) || 0;
    const max = track.scrollWidth - track.clientWidth;
    const lo = rtl ? -max : 0, hi = rtl ? 0 : max;
    let best = track.scrollLeft, bestD = Infinity;
    kids.forEach((k) => {
      const kr = k.getBoundingClientRect();
      const delta = rtl ? (kr.right - tr.right + padStart) : (kr.left - tr.left - padStart);
      const target = Math.min(hi, Math.max(lo, Math.round(track.scrollLeft + delta)));
      const d = Math.abs(target - track.scrollLeft);
      if (d < bestD) { bestD = d; best = target; }
    });
    return best;
  };

  /* ---- Reveal-on-scroll (specs mesurées apple.com/os/ios) ---------------- */
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

  function initReveals(root) {
    $$('[data-osyn-reveal]', root).forEach((el) => { if (once(el)) revealIO.observe(el); });
    /* groupes : stagger 100ms automatique entre enfants directs */
    $$('[data-osyn-reveal-group]', root).forEach((group) => {
      if (!once(group)) return;
      [...group.children].forEach((child, i) => {
        if (!once(child)) return;
        child.setAttribute('data-osyn-reveal', '');
        child.style.setProperty('--reveal-delay', `${i * 100}ms`);
        revealIO.observe(child);
      });
    });
  }

  /* ---- Amorce de défilement (mobile) ---------------------------------------------------
     Une fois par rail, 520 ms après son entrée à 60 % dans l'écran, le rail défile VRAIMENT de
     40 % d'un pas de carte puis revient (980 ms : 55 % aller, 45 % retour, courbe 1-(1-t)^3) :
     il montre qu'il se fait glisser. Référence Bang Bang (cluutch-pdp-2026.js), avec six
     corrections : (1) un geste pendant le mouvement fige le rail sous le doigt puis recale en
     douceur sur la carte la plus proche (rétablir le snap au contact le faisait sauter) ;
     (2) sens RTL ; (3) consommée seulement si jouée ; (4) un rail à la fois ; (5) défilement
     instantané pendant le mouvement ; (6) focus clavier = annulation. Rien sous
     prefers-reduced-motion, ni ≥ 1024 px, ni sur un rail qui ne déborde pas, ni page masquée.
     Déclaratif : [data-osyn-amorce] sur la piste d'un slider du moteur ; un moteur maison
     (transform) passe son propre pilote à OsynUI.amorce(el, driver). */
  const AMORCE = Object.freeze({ delay: 520, duration: 980, out: 0.55, ratio: 0.4, threshold: 0.6, queue: 300, silence: 120, settle: 700, mq: '(max-width: 1023px)' });
  const amorceEase = (t) => 1 - Math.pow(1 - t, 3);
  const amorceMq = window.matchMedia(AMORCE.mq);
  let amorceBusy = false;

  /* Pilote par défaut : piste à défilement natif. Contrat d'un pilote : rtl, position(),
     measure() → { pitch, room }, begin(), frame(offset), end(), release(), settle().
     Pendant le mouvement, data-osyn-amorce-live fige la pagination (sur tablette, 40 % d'un
     pas suffit à faire basculer l'index calculé en fraction du défilement total). */
  const scrollDriver = (track) => {
    const rtl = getComputedStyle(track).direction === 'rtl';
    let rest = 0, snap = '', behavior = '';
    const restore = () => { track.style.scrollSnapType = snap; track.style.scrollBehavior = behavior; };
    return {
      rtl,
      position: () => track.scrollLeft,
      measure: () => {
        const kids = track.children;
        const pitch = kids.length > 1 ? Math.abs(kids[1].getBoundingClientRect().left - kids[0].getBoundingClientRect().left) : 0;
        return { pitch, room: track.scrollWidth - track.clientWidth - Math.abs(track.scrollLeft) };
      },
      begin: () => {
        rest = track.scrollLeft; snap = track.style.scrollSnapType; behavior = track.style.scrollBehavior;
        track.style.scrollSnapType = 'none'; track.style.scrollBehavior = 'auto';
        track.dataset.osynAmorceLive = '1';
      },
      frame: (off) => { track.scrollLeft = rest + off; },
      end: () => { track.scrollLeft = rest; delete track.dataset.osynAmorceLive; restore(); },
      release: () => { delete track.dataset.osynAmorceLive; },
      settle: () => {
        let done = false;
        const finish = () => { if (!done) { done = true; restore(); } };
        track.addEventListener('scrollend', finish, { once: true });
        setTimeout(finish, AMORCE.settle);
        track.scrollTo({ left: snapTarget(track, rtl), behavior: 'smooth' });
      },
    };
  };

  function armAmorce(el, driver) {
    if (reduce || !driver || el.dataset.osynAmorceArmed || !('IntersectionObserver' in window)) return;
    el.dataset.osynAmorceArmed = '1';
    const signal = signalFor(el);
    let ratio = 0, timer = null, raf = null, entryPos = null;
    let touched = false, played = false, playing = false;

    /* geste pendant le mouvement : plus aucune écriture (le rail reste sous le doigt), puis
       recalage doux après 120 ms sans défilement ni contact */
    const cut = (pointerHeld, touch) => {
      playing = false; amorceBusy = false; cancelAnimationFrame(raf);
      if (driver.release) driver.release();
      let quiet = null, held = pointerHeld;
      const opts = { passive: true, signal };
      /* au doigt, pointercancel part dès que le navigateur prend le défilement, doigt encore posé :
         seul touchend/touchcancel sans plus aucun contact vaut relâché */
      const ends = touch ? ['touchend', 'touchcancel'] : ['pointerup', 'pointercancel'];
      const release = (e) => { if (e.touches && e.touches.length) return; held = false; wait(); };
      const wait = () => {
        clearTimeout(quiet);
        quiet = setTimeout(() => {
          if (held) return;
          el.removeEventListener('scroll', wait);
          ends.forEach((t) => window.removeEventListener(t, release));
          driver.settle();
        }, AMORCE.silence);
      };
      el.addEventListener('scroll', wait, opts);
      ends.forEach((t) => window.addEventListener(t, release, opts));
      wait();
    };
    const gesture = (e) => {
      touched = true;
      clearTimeout(timer); timer = null;
      if (playing) cut(e.type === 'pointerdown' || e.type === 'touchstart', e.type === 'touchstart' || e.pointerType === 'touch');
    };
    ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach((t) => el.addEventListener(t, gesture, { passive: true, signal }));

    const play = (amp) => {
      played = true; playing = true; amorceBusy = true;
      const dir = driver.rtl ? -1 : 1;
      let t0 = null;
      driver.begin();
      const step = (t) => {
        if (!playing) return;
        if (t0 === null) t0 = t;
        const a = Math.min(1, (t - t0) / AMORCE.duration);
        const d = a < AMORCE.out ? amorceEase(a / AMORCE.out) : 1 - amorceEase((a - AMORCE.out) / (1 - AMORCE.out));
        driver.frame(dir * amp * d);
        if (a < 1) { raf = requestAnimationFrame(step); return; }
        playing = false; amorceBusy = false;
        driver.end();
      };
      raf = requestAnimationFrame(step);
    };
    const fire = () => {
      timer = null;
      if (played || touched || !amorceMq.matches || document.hidden) return;
      if (ratio < AMORCE.threshold) return;                                        /* plus assez visible : prochaine entrée */
      if (Math.abs(driver.position() - entryPos) > 1) { touched = true; return; }  /* déjà bougé depuis l'entrée */
      if (amorceBusy) { timer = setTimeout(fire, AMORCE.queue); return; }           /* un rail à la fois */
      const { pitch, room } = driver.measure();
      const amp = Math.min(Math.round(pitch * AMORCE.ratio), Math.floor(room));
      if (!pitch || amp < 8) return;
      play(amp);
    };
    const io = new IntersectionObserver(([e]) => {
      ratio = e.intersectionRatio;
      if (e.isIntersecting && ratio >= AMORCE.threshold && !timer && !played && !touched) {
        entryPos = driver.position();
        timer = setTimeout(fire, AMORCE.delay);
      }
    }, { threshold: [0, AMORCE.threshold, 1] });
    io.observe(el);
    signal.addEventListener('abort', () => {
      io.disconnect(); clearTimeout(timer); cancelAnimationFrame(raf);
      if (playing) { playing = false; amorceBusy = false; }
    }, { once: true });
  }

  /* ---- Sliders : snap + drag + flèches + pagination morphante ------------- */
  function initSliders(root) {
    $$('[data-osyn-slider]', root).forEach((slider) => {
      if (!once(slider)) return;
      const track = $('[data-osyn-track]', slider) || slider.querySelector('ul, .osyn-slider__track');
      const dotsBox = $('[data-osyn-dots]', slider);
      const scope = slider.closest('section') || slider.parentElement;
      const prev = $('[data-osyn-prev]', scope);
      const next = $('[data-osyn-next]', scope);
      if (!track) return;

      const cards = [...track.children];
      const gap = () => parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      const step = () => {
        const c = track.firstElementChild;
        return c ? c.getBoundingClientRect().width + gap() : track.clientWidth * 0.8;
      };

      /* RTL : position LOGIQUE unique. En RTL (spec moderne Chrome/FF),
         scrollLeft ∈ [-max, 0] avec 0 = début (bord droit). pos() = progression
         0→max quel que soit le sens ; toX() reconvertit une position logique en
         scrollLeft physique. En LTR les deux sont l'identité : zéro changement
         de comportement. Drag & momentum (deltas physiques) restent tels quels,
         ils sont corrects dans les deux sens. Clavier : mapping VISUEL conservé
         (convention carrousels W3C) ; boutons prev/next : avancée LOGIQUE. */
      const rtl = getComputedStyle(track).direction === 'rtl';
      const pos = () => Math.abs(track.scrollLeft);
      const toX = (logical) => (rtl ? -logical : logical);
      const fwd = () => (rtl ? -1 : 1); // avancée logique en coordonnée physique

      let dots = [];
      if (dotsBox && cards.length > 1) {
        dotsBox.classList.add('osyn-pgn');
        const pill = document.createElement('div');
        pill.className = 'osyn-pgn__pill';
        cards.forEach((_, i) => {
          const b = document.createElement('button');
          b.className = 'osyn-pdot';
          b.type = 'button';
          b.setAttribute('aria-label', fmt(T.dot, { i: i + 1, n: cards.length }));
          b.addEventListener('click', () => {
            const max = track.scrollWidth - track.clientWidth;
            track.scrollTo({ left: toX(cards.length > 1 ? (i / (cards.length - 1)) * max : 0), behavior: 'smooth' });
          });
          pill.appendChild(b);
          dots.push(b);
        });
        dotsBox.appendChild(pill);
      }

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = pos();
        if (prev) prev.disabled = x <= 2;
        if (next) next.disabled = x >= max - 2;
        if (dots.length && !track.dataset.osynAmorceLive) {
          const frac = max > 0 ? x / max : 0;
          const idx = Math.min(dots.length - 1, Math.max(0, Math.round(frac * (dots.length - 1))));
          dots.forEach((d, i) => {
            d.classList.toggle('is-active', i === idx);
            if (i === idx) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
          });
        }
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() * fwd(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step() * fwd(), behavior: 'smooth' }));

      track.tabIndex = 0;
      track.setAttribute('role', 'group');
      track.setAttribute('aria-roledescription', T.roledescription);
      const heading = scope.querySelector('h2');
      if (heading) {
        if (!heading.id) heading.id = `osyn-h-${Math.random().toString(36).slice(2, 9)}`;
        track.setAttribute('aria-labelledby', heading.id);
      } else if (!track.hasAttribute('aria-label')) {
        track.setAttribute('aria-label', T.label);
      }
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); track.scrollBy({ left: step(), behavior: 'smooth' }); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); track.scrollBy({ left: -step(), behavior: 'smooth' }); }
      });

      // Drag souris : suivi 1:1 + momentum décéléré + alignement doux (façon Swiper).
      // Le tactile garde le scroll natif + snap CSS (non intercepté : gate pointerType mouse).
      let down = false, startX = 0, startLeft = 0, moved = false;
      let lastX = 0, lastT = 0, vx = 0, momRAF = null, snapBackT = null;
      const stopMomentum = () => { if (momRAF) { cancelAnimationFrame(momRAF); momRAF = null; } };
      const settleSnap = () => {
        clearTimeout(snapBackT);
        snapBackT = setTimeout(() => { if (!down && momRAF === null) { track.style.scrollSnapType = ''; track.style.scrollBehavior = ''; } }, 500);
      };
      // aligne sur la carte la plus proche — mesuré au rect réel (robuste quel que soit le scroll-padding).
      // Direction-aware : bord de référence = inline-start (gauche en LTR, droite en RTL),
      // clamp dans la plage physique réelle ([0,max] en LTR, [-max,0] en RTL).
      const snapNearest = () => {
        if (!track.children.length) return;
        track.scrollTo({ left: snapTarget(track, rtl), behavior: reduce ? 'auto' : 'smooth' });
      };
      const momentum = () => {
        track.scrollLeft -= vx * 16;          // vx en px/ms → déplacement par frame (~16ms)
        vx *= 0.94;                            // friction (décélération)
        if (Math.abs(vx) > 0.04) { momRAF = requestAnimationFrame(momentum); }
        else { momRAF = null; snapNearest(); settleSnap(); }
      };
      track.addEventListener('dragstart', (e) => e.preventDefault()); // tue le drag fantôme image/lien
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        e.preventDefault();                    // pas de sélection de texte ni de drag natif
        stopMomentum(); clearTimeout(snapBackT);
        down = true; moved = false;
        startX = lastX = e.clientX; startLeft = track.scrollLeft; lastT = e.timeStamp; vx = 0;
        /* PAS de setPointerCapture ici : posée au pointerdown, elle redirige pointerup ET click vers
           la piste, et un lien de carte n'est jamais suivi à la souris ou au trackpad (mesuré
           Chromium + WebKit). Capture posée au premier vrai glissé (pointermove > 4 px). */
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto'; // suivi 1:1 instantané (sinon scroll-behavior:smooth anime chaque scrollLeft)
        track.style.cursor = 'grabbing';
        /* sans capture, un relâché hors du rail ne lui parvient pas : la fenêtre termine le geste */
        window.addEventListener('pointerup', end, { once: true });
        window.addEventListener('pointercancel', end, { once: true });
      });
      track.addEventListener('pointermove', (e) => {
        if (!down) return;
        if (!(e.buttons & 1)) { end(); return; }   // bouton relâché ailleurs : ne pas suivre le simple survol
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 4) {
          moved = true;
          try { track.setPointerCapture(e.pointerId); } catch (_) { /* pointeur déjà relâché */ }
        }
        track.scrollLeft = startLeft - dx;
        const dt = e.timeStamp - lastT;
        if (dt > 0) { vx = 0.8 * ((e.clientX - lastX) / dt) + 0.2 * vx; lastX = e.clientX; lastT = e.timeStamp; }
      });
      function end() {
        if (!down) return;
        down = false; track.style.cursor = '';
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        if (moved && !reduce && Math.abs(vx) > 0.05) { momRAF = requestAnimationFrame(momentum); }
        else { snapNearest(); settleSnap(); }
      }
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      track.addEventListener('click', (e) => { if (moved) e.preventDefault(); }, true);

      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      if (track.hasAttribute('data-osyn-amorce')) armAmorce(track, scrollDriver(track));
      let rT;
      window.addEventListener('resize', () => { clearTimeout(rT); rT = setTimeout(update, 150); }, { signal: signalFor(slider) });
      update();
    });
  }

  /* ---- Onglets : indicateur coulissant (320ms ease-apple) ----------------- */
  function initTabs(root) {
    $$('[data-osyn-tabs]', root).forEach((tabsRoot) => {
      if (!once(tabsRoot)) return;
      const nav = $('.osyn-tabs', tabsRoot) || tabsRoot;
      const tabs = $$('.osyn-tab', tabsRoot);
      const panels = $$('[data-osyn-panel]', tabsRoot.closest('[data-osyn-tabs-scope]') || tabsRoot.parentElement || document);
      if (!tabs.length) return;

      const ind = document.createElement('span');
      ind.className = 'osyn-tabs__indicator';
      ind.setAttribute('aria-hidden', 'true');
      nav.prepend(ind);

      const moveIndicator = (tab, animate = true) => {
        if (!animate || reduce) ind.style.transition = 'none';
        else ind.style.transition = '';
        ind.style.transform = `translateX(${tab.offsetLeft}px)`;
        ind.style.width = tab.offsetWidth + 'px';
        if (!animate || reduce) requestAnimationFrame(() => { ind.style.transition = ''; });
      };

      const activate = (key, focus = false) => {
        tabs.forEach((t) => {
          const on = t.dataset.osynTab === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          if (on) { moveIndicator(t); if (focus) t.focus(); }
        });
        panels.forEach((p) => {
          const on = p.dataset.osynPanel === key;
          if (on) {
            p.hidden = false;
            if (!reduce) p.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 280, easing: 'cubic-bezier(0,0,.5,1)' });
          } else { p.hidden = true; }
        });
      };

      tabs.forEach((t, i) => {
        t.addEventListener('click', () => activate(t.dataset.osynTab));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const dir = e.key === 'ArrowRight' ? 1 : -1;
            activate(tabs[(i + dir + tabs.length) % tabs.length].dataset.osynTab, true);
          }
        });
      });

      const current = tabs.find((t) => t.classList.contains('is-active')) || tabs[0];
      activate(current.dataset.osynTab);
      moveIndicator(current, false);
      let rT;
      window.addEventListener('resize', () => {
        clearTimeout(rT);
        rT = setTimeout(() => {
          const active = tabs.find((t) => t.classList.contains('is-active'));
          if (active) moveIndicator(active, false);
        }, 150);
      }, { signal: signalFor(tabsRoot) });
    });
  }

  /* ---- Marquee (WAAPI) + play/pause Apple ---------------------------------- */
  function initMarquees(root) {
    $$('[data-osyn-marquee]', root).forEach((mq) => {
      if (!once(mq)) return;
      const anims = [];
      let paused = reduce;
      $$('[data-osyn-marquee-row]', mq).forEach((row) => {
        if (reduce) return; /* pas de clonage ni d'animation en reduced-motion */
        [...row.children].forEach((child) => row.appendChild(child.cloneNode(true)));
        const dir = row.dataset.osynMarqueeRow === 'right' ? 1 : -1;
        const dist = row.scrollWidth / 2;
        const anim = row.animate(
          dir < 0
            ? [{ transform: 'translateX(0)' }, { transform: `translateX(-${dist}px)` }]
            : [{ transform: `translateX(-${dist}px)` }, { transform: 'translateX(0)' }],
          { duration: (dist / 40) * 1000, iterations: Infinity, easing: 'linear' }
        );
        anims.push(anim);
        row.parentElement.addEventListener('mouseenter', () => anim.pause());
        row.parentElement.addEventListener('mouseleave', () => { if (!paused) anim.play(); });
      });
      const toggle = $('[data-osyn-marquee-toggle]', mq.closest('section') || document);
      if (toggle) {
        const icoPause = $('[data-osyn-ico-pause]', toggle);
        const icoPlay = $('[data-osyn-ico-play]', toggle);
        const apply = () => {
          anims.forEach((a) => (paused ? a.pause() : a.play()));
          if (icoPause) icoPause.hidden = paused;
          if (icoPlay) icoPlay.hidden = !paused;
          toggle.setAttribute('aria-pressed', String(paused));
          toggle.setAttribute('aria-label', paused ? T.play : T.pause);
        };
        if (reduce) apply();
        toggle.addEventListener('click', () => { paused = !paused; apply(); });
      }
    });
  }

  /* ---- Compteurs (count-up ease-out au reveal ; data-osyn-count-duration) --- */
  function initCounters(root) {
    $$('[data-osyn-count]', root).forEach((el) => {
      if (!once(el)) return;
      const target = parseInt(el.dataset.osynCount, 10);
      if (Number.isNaN(target)) return;
      const dur = parseInt(el.dataset.osynCountDuration || '600', 10);
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        if (reduce || target === 0) { el.textContent = String(target); return; }
        const t0 = performance.now();
        const tick = (t) => {
          const k = Math.min(1, (t - t0) / dur);
          el.textContent = String(Math.round((1 - Math.pow(1 - k, 3)) * target));
          if (k < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  /* ---- Stage produit : scrub amorti (utilisé par la future section home) ---- */
  function initStages(root) {
    $$('[data-osyn-stage]', root).forEach((stage) => {
      if (!once(stage)) return;
      const track = $('[data-osyn-stage-track]', stage);
      const media = $('[data-osyn-stage-media]', stage);
      const callouts = $$('[data-osyn-callout]', stage);
      const railSteps = $$('[data-osyn-rail] li', stage);
      const thumb = $('[data-osyn-rail-thumb]', stage);
      if (!track || !media) return;
      if (reduce) { callouts.forEach((c) => c.classList.add('is-visible')); railSteps.forEach((r) => r.classList.add('is-on')); return; }
      let target = 0, p = 0, raf = null;
      const readTarget = () => {
        const rect = track.getBoundingClientRect();
        target = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
      };
      const apply = () => {
        p += (target - p) * 0.1;
        if (Math.abs(target - p) < 0.0005) p = target;
        const chapter = Math.min(railSteps.length - 1 || 3, Math.floor(p * (railSteps.length || callouts.length || 4)));
        const drift = (chapter % 2 === 0 ? -1 : 1) * 8 * Math.sin(p * Math.PI);
        media.style.transform = `translateX(${drift.toFixed(2)}px) scale(${(1 + p * 0.06).toFixed(4)})`;
        if (thumb) {
          const max = thumb.parentElement.clientHeight - thumb.offsetHeight;
          thumb.style.transform = `translate3d(0, ${(p * Math.max(0, max)).toFixed(2)}px, 0)`;
        }
        callouts.forEach((c) => {
          c.classList.toggle('is-visible', p >= parseFloat(c.dataset.in) && p < parseFloat(c.dataset.out));
        });
        railSteps.forEach((r, i) => r.classList.toggle('is-on', i === chapter));
        if (p !== target) raf = requestAnimationFrame(apply); else raf = null;
      };
      const onScroll = () => { readTarget(); if (raf === null) raf = requestAnimationFrame(apply); };
      const signal = signalFor(stage);
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', onScroll, { signal });
      onScroll();
    });
  }

  /* ---- Toggle / switch (iOS) — synchronise l'input hidden adjacent ---------- */
  function initToggles(root) {
    $$('[data-osyn-toggle]', root).forEach((t) => {
      if (!once(t)) return;
      const sib = t.nextElementSibling;
      const hidden = sib && sib.hasAttribute('data-osyn-toggle-input') ? sib : null;
      const sync = () => {
        if (hidden) hidden.value = t.getAttribute('aria-checked') === 'true' ? (hidden.dataset.on || '1') : '';
      };
      t.addEventListener('click', () => {
        if (t.disabled) return;
        const on = t.getAttribute('aria-checked') === 'true';
        t.setAttribute('aria-checked', String(!on));
        sync();
      });
      sync();
    });
  }

  /* ---- Quantité (− / +) — min/max + synchronise l'input hidden --------------- */
  function initQtys(root) {
    $$('[data-osyn-qty]', root).forEach((q) => {
      if (!once(q)) return;
      const val = $('[data-osyn-qty-value]', q);
      const dec = $('[data-osyn-qty-dec]', q);
      const inc = $('[data-osyn-qty-inc]', q);
      const hidden = $('[data-osyn-qty-input]', q);
      if (!val || !dec || !inc) return;
      const min = parseInt(q.dataset.min || '1', 10);
      const max = q.dataset.max ? parseInt(q.dataset.max, 10) : Infinity;
      const set = (n) => {
        const v = Math.min(max, Math.max(min, n));
        val.textContent = String(v);
        dec.disabled = v <= min;
        inc.disabled = v >= max;
        if (hidden) hidden.value = String(v);
      };
      dec.addEventListener('click', () => set(parseInt(val.textContent, 10) - 1));
      inc.addEventListener('click', () => set(parseInt(val.textContent, 10) + 1));
      set(parseInt(val.textContent, 10));
    });
  }

  /* ---- Accordéon -------------------------------------------------------------- */
  function initAccordions(root) {
    $$('[data-osyn-accordion]', root).forEach((box) => {
      if (!once(box)) return;
      $$('.osyn-acc', box).forEach((acc) => {
        const q = $('.osyn-acc__q', acc);
        const a = $('.osyn-acc__a', acc);
        if (!q || !a) return;
        q.addEventListener('click', () => {
          const open = q.getAttribute('aria-expanded') === 'true';
          q.setAttribute('aria-expanded', String(!open));
          a.style.height = open ? '0px' : a.scrollHeight + 'px';
        });
      });
    });
  }
  /* re-mesure des panneaux ouverts au resize (écouteur global unique) */
  let accRT;
  window.addEventListener('resize', () => {
    clearTimeout(accRT);
    accRT = setTimeout(() => {
      $$('.osyn-acc__q[aria-expanded="true"]').forEach((q) => {
        const a = q.parentElement && q.parentElement.querySelector('.osyn-acc__a');
        if (a) a.style.height = a.scrollHeight + 'px';
      });
    }, 150);
  });

  /* ---- Démos styleguide : rejouer les états boutons · formulaire inerte ------- */
  function initDemos(root) {
    $$('[data-osyn-replay]', root).forEach((btn) => {
      if (!once(btn)) return;
      btn.addEventListener('click', () => {
        const targets = $$(btn.dataset.osynReplay || '.osyn-btn', btn.closest('section') || document);
        let step = 0;
        const states = ['is-hover', 'is-active', ''];
        const tick = () => {
          targets.forEach((t) => t.classList.remove('is-hover', 'is-active'));
          if (step < states.length && states[step]) targets.forEach((t) => t.classList.add(states[step]));
          step += 1;
          if (step <= states.length) setTimeout(tick, 600);
        };
        tick();
      });
    });
    $$('form[data-osyn-demo-form]', root).forEach((f) => {
      if (once(f)) f.addEventListener('submit', (e) => e.preventDefault());
    });
  }

  /* ---- Ids de champs : dédoublonnage déterministe (label + aria re-pointés) --- */
  function fixDuplicateFieldIds() {
    const seen = new Set();
    $$('.osyn-field .osyn-input[id]').forEach((input) => {
      if (!seen.has(input.id)) { seen.add(input.id); return; }
      const oldId = input.id;
      let n = 2, next = `${oldId}-${n}`;
      while (seen.has(next) || document.getElementById(next)) { n += 1; next = `${oldId}-${n}`; }
      const field = input.closest('.osyn-field');
      const label = field && field.querySelector(`label[for="${oldId}"]`);
      if (input.getAttribute('aria-describedby') === `${oldId}-err`) {
        const err = field && field.querySelector(`[id="${oldId}-err"]`);
        if (err) err.id = `${next}-err`;
        input.setAttribute('aria-describedby', `${next}-err`);
      }
      input.id = next;
      if (label) label.htmlFor = next;
      seen.add(next);
    });
  }

  /* ---- Init globale + cycle de vie éditeur de thème --------------------------- */
  function initOsyn(root = document) {
    fixDuplicateFieldIds();
    initReveals(root);
    initSliders(root);
    initTabs(root);
    initMarquees(root);
    initCounters(root);
    initStages(root);
    initToggles(root);
    initQtys(root);
    initAccordions(root);
    initDemos(root);
  }

  /* Précommande : mention sur la ligne du panier, reprise au paiement et sur la commande (variantes et textes : osyn-head).
     À la capture : le formulaire de Concept lit ses champs à l'envoi. */
  document.addEventListener('submit', (e) => {
    const form = e.target, pre = window.OsynPrecommande;
    if (!pre || !form.matches('form[action*="/cart/add"]') || !form.elements.id) return;
    if (!pre.variantes.includes(Number(form.elements.id.value)) || $('[data-osyn-precommande]', form)) return;
    const champ = Object.assign(document.createElement('input'), { type: 'hidden', name: `properties[${pre.nom}]`, value: pre.valeur });
    champ.dataset.osynPrecommande = '';
    form.append(champ);
  }, true);

  initOsyn();
  document.addEventListener('shopify:section:load', (e) => initOsyn(e.target));
  window.OsynUI = { init: initOsyn, amorce: armAmorce, snapTarget };
})();
